import { TimelineGroupBase } from "react-calendar-timeline";

import moment, { Moment } from "moment";

import { getCurrentReportingValues } from "utils";

import {
  CROP_TYPE_FCPO,
  CarbonProgram,
  CarbonProgramResult,
  CropFCPO,
  CropItemData,
  FieldOutcomesByPractice,
  FieldPracticeResponse,
  InvalidReason,
  PRACTICE_SLUG,
  PracticeFCPO,
  PracticeSlug,
} from "models";

import { isCoverCropMissingRedzoneException, isRelevantYear } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import { GROUP_TYPE, PRACTICE_GROUPS, PRACTICE_TYPE, TimelineCalendarItemsProps } from "pages/app/field-timeline/components/FieldDetailsTimeline.tsx";
import { groupPracticeItemsByFourWeeks } from "pages/app/field-timeline/utils/PracticeUtils.ts";

type MainCrop = { cropId: string; harvestEndDate?: string; seedingStartDate?: string } | null;
export type CMCMainCrops = { before?: MainCrop; after?: MainCrop };

export const isDateValidConsideringLastConfirmedCrop = (
  startTime: Moment,
  endTime: Moment,
  crops: CropFCPO[] | undefined,
  cropType: CROP_TYPE_FCPO,
  reportingYear: number | undefined
): boolean => {
  if (!crops) return false;
  if (!startTime.isValid() || !endTime.isValid()) return false;

  const confirmedCrops = crops.filter((crop) => crop.isConfirmed);
  const lastConfirmedCropYear = Math.max(...(confirmedCrops.map((crop) => crop.year).filter((year) => year !== undefined) as number[]));

  const startTimeYear = startTime.clone().year();
  const endTimeYear = endTime.clone().year();

  if (!confirmedCrops.length) return true;

  if (cropType === CROP_TYPE_FCPO.MAIN) {
    // ---------------------
    // MAIN CROP VALIDATION
    // ---------------------

    // new crop
    if (!reportingYear) {
      // must be added after the last confirmed year
      if (endTimeYear > lastConfirmedCropYear) return true;

      // existing crop
    } else {
      // if reporting year is after the last confirmed year we allow editing as long as the end date is after
      // the last confirmed year
      if (reportingYear > lastConfirmedCropYear && endTimeYear > lastConfirmedCropYear) return true;

      // if reporting year is sooner or equal to last confirmed year, we allow editing as long as it stays in
      // the same reporting year
      if (reportingYear <= lastConfirmedCropYear && reportingYear === endTimeYear) return true;
    }

    return false;

    // TODO: check if this logic is still needed
    const confirmedMainCrops = confirmedCrops.filter((crop) => crop.cropType === CROP_TYPE_FCPO.MAIN);

    // Get the last confirmed main crop based on harvestDate (most recent)
    const lastConfirmationDate = moment(
      confirmedMainCrops.sort((a, b) => moment(b.harvestEndDate).valueOf() - moment(a.harvestEndDate).valueOf())?.[0]?.harvestEndDate
    );

    if (startTime.isBefore(lastConfirmationDate)) return false;
    if (endTime.year() === moment(lastConfirmationDate).year()) return false;
    const endOfTheYear = moment(lastConfirmationDate).endOf("year");
    const isBetweenLastConfirmationDateAndEndOfThatYear = startTime.isBetween(lastConfirmationDate, endOfTheYear, "day", "[]");
    if (!isBetweenLastConfirmationDateAndEndOfThatYear) return true;

    // New rule: Check if inserting between two main crops
    const previousMainCrop = confirmedMainCrops
      .filter((crop) => moment(crop.harvestEndDate).isSameOrBefore(startTime))
      .sort((a, b) => moment(b.harvestEndDate).valueOf() - moment(a.harvestEndDate).valueOf())[0];

    const nextMainCrop = confirmedMainCrops
      .filter((crop) => moment(crop.harvestEndDate).isAfter(startTime))
      .sort((a, b) => moment(a.harvestEndDate).valueOf() - moment(b.harvestEndDate).valueOf())[0];

    if (previousMainCrop && nextMainCrop) {
      const nextCropStartYear = moment(nextMainCrop.seedingStartDate).year();
      const nextCropEndYear = moment(nextMainCrop.harvestEndDate).year();

      // allow insertion only if the next crop spans multiple years & the end time is in the same year as the next crop start year

      const isNextCropSpanningMultipleYears = endTime.year() === nextCropStartYear && nextCropEndYear !== nextCropStartYear;

      if (
        startTime.isAfter(moment(previousMainCrop.harvestEndDate)) &&
        endTime.isBefore(moment(nextMainCrop.seedingStartDate)) &&
        !isNextCropSpanningMultipleYears
      ) {
        return false;
      }
    }

    // main crop fallback
    return true;
  }

  if (cropType === CROP_TYPE_FCPO.COVER || cropType === CROP_TYPE_FCPO.UNDERSOWING) {
    // -----------------------------
    // SIDE CROP VALIDATION using seedingDate
    // -----------------------------

    // new crop
    if (!reportingYear) {
      // must be added after the last confirmed year
      if (startTimeYear > lastConfirmedCropYear) return true;

      // existing crop
    } else {
      // if reporting year is after the last confirmed year we allow editing as long as the end date is after
      // the last confirmed year
      if (reportingYear > lastConfirmedCropYear && startTimeYear > lastConfirmedCropYear) return true;

      // if reporting year is sooner or equal to last confirmed year, we allow editing as long as it stays in
      // the same reporting year
      if (reportingYear <= lastConfirmedCropYear && reportingYear === startTimeYear) return true;
    }

    return false;
  }

  // fallback
  return false;
};

const getFillerGroupHeight = (groups: CustomTimelineGroup[], timelineContainerHeight: number): number => {
  const groupsHeight = groups.reduce(
    (totalGroupsHeight, curr) => (curr.id !== GROUP_TYPE.FILLER && curr.height ? totalGroupsHeight + curr.height : totalGroupsHeight),
    0
  );

  return timelineContainerHeight - groupsHeight;
};

export const getGroupsToShow = (groups: CustomTimelineGroup[], hiddenGroups: GROUP_TYPE[], timelineContainerHeight: number) => {
  if (!groups || groups.length === 0) return [];

  const filteredGroups = groups.filter((group) => !hiddenGroups.includes(group.id as GROUP_TYPE));
  const groupFillerHeight = getFillerGroupHeight(filteredGroups, timelineContainerHeight);
  const lastPracticeGroupsIds = Object.values(PRACTICE_GROUPS).map(
    (practiceGroupIds) => practiceGroupIds.filter((id) => filteredGroups.some((group) => group.id === id)).sort((a, b) => b - a)[0]
  );
  const groupsToShow = filteredGroups.map((group) => {
    if (group.id === GROUP_TYPE.FILLER) {
      return { ...group, height: groupFillerHeight, isLastOfGroup: true };
    }

    return { ...group, isLastOfGroup: lastPracticeGroupsIds.includes(group.id as GROUP_TYPE) };
  });

  return groupsToShow;
};

export type CustomTimelineGroup = TimelineGroupBase & { width: number; isLastOfGroup?: boolean };
export const createGroups = (fieldPracticeResponse: FieldPracticeResponse): CustomTimelineGroup[] => {
  const SIDEBAR_GROUP_WIDTH = 20;
  const CROP_HEIGHT = 40;
  const PRACTICE_HEIGHT = 32;
  const PADDING = 16;
  const MAX_NUMBER_OF_PRACTICES_ROWS_PER_GROUP = 4;

  const getPracticeHeight = (length: number) => {
    if (length <= 1) return PRACTICE_HEIGHT + PADDING;
    if (length > MAX_NUMBER_OF_PRACTICES_ROWS_PER_GROUP) return PRACTICE_HEIGHT * MAX_NUMBER_OF_PRACTICES_ROWS_PER_GROUP + PADDING;
    return PRACTICE_HEIGHT * length + PADDING;
  };

  const getGroupHeight = (practiceFCPOS: PracticeFCPO[]): number => {
    if (!practiceFCPOS || practiceFCPOS.length === 0) return getPracticeHeight(1);

    const groups = groupPracticeItemsByFourWeeks(practiceFCPOS);
    const maxNumberOfItems = Math.max(...Array.from(groups.values()).map((group) => group.length));
    return getPracticeHeight(maxNumberOfItems);
  };

  const practiceCategories: Record<string, PracticeSlug[]> = {
    tillage: [PRACTICE_SLUG.TILLAGES],
    fertilizer: [PRACTICE_SLUG.ORGANIC_FERTILIZERS, PRACTICE_SLUG.MINERAL_FERTILIZERS],
  };
  const getPracticeAssociatedCropType = (cropsList: CropFCPO[], associatedCropId: string) => cropsList.find((crop) => crop.id === associatedCropId)?.cropType;
  const getCategoryKey = (practiceType: PracticeFCPO["type"]): keyof typeof practiceCategories | undefined =>
    Object.entries(practiceCategories).find(([, practiceTypes]) => practiceTypes.includes(practiceType))?.[0] as keyof typeof practiceCategories;

  const tillageAndFertilizerList = fieldPracticeResponse.practices.reduce<
    Record<keyof typeof practiceCategories, { mainCropList: PracticeFCPO[]; sideCropList: PracticeFCPO[] }>
  >(
    (acc, practice) => {
      const category = getCategoryKey(practice.type);
      if (!category || !(category in acc)) return acc;

      if (!practice.cropId) {
        acc[category].mainCropList.push(practice);
        return acc;
      }

      const cropType = getPracticeAssociatedCropType(fieldPracticeResponse.crops, practice.cropId);
      if (!cropType || cropType === CROP_TYPE_FCPO.MAIN) {
        acc[category].mainCropList.push(practice);
        return acc;
      }
      if (cropType === CROP_TYPE_FCPO.UNDERSOWING || cropType === CROP_TYPE_FCPO.COVER) {
        acc[category].sideCropList.push(practice);
        return acc;
      }
      return acc;
    },
    {
      tillage: { mainCropList: [], sideCropList: [] },
      fertilizer: { mainCropList: [], sideCropList: [] },
    }
  );

  // hide side crop practices if there is no side crop?
  return [
    { id: GROUP_TYPE.MAIN_CROP, title: "", height: CROP_HEIGHT + PADDING, width: SIDEBAR_GROUP_WIDTH },
    { id: GROUP_TYPE.MAIN_CROP_TILLAGE, title: "", height: getGroupHeight(tillageAndFertilizerList.tillage.mainCropList), width: SIDEBAR_GROUP_WIDTH },
    { id: GROUP_TYPE.MAIN_CROP_SEEDING, title: "", height: getPracticeHeight(1), width: SIDEBAR_GROUP_WIDTH },
    { id: GROUP_TYPE.MAIN_CROP_FERTILIZER, title: "", height: getGroupHeight(tillageAndFertilizerList.fertilizer.mainCropList), width: SIDEBAR_GROUP_WIDTH },
    { id: GROUP_TYPE.SIDE_CROP, title: "", height: CROP_HEIGHT + PADDING, width: SIDEBAR_GROUP_WIDTH },
    { id: GROUP_TYPE.SIDE_CROP_TILLAGE, title: "", height: getGroupHeight(tillageAndFertilizerList.tillage.sideCropList), width: SIDEBAR_GROUP_WIDTH },
    { id: GROUP_TYPE.SIDE_CROP_SEEDING, title: "", height: getPracticeHeight(1), width: SIDEBAR_GROUP_WIDTH },
    { id: GROUP_TYPE.SIDE_CROP_FERTILIZER, title: "", height: getGroupHeight(tillageAndFertilizerList.fertilizer.sideCropList), width: SIDEBAR_GROUP_WIDTH },
    { id: GROUP_TYPE.FILLER, title: "", height: 0, width: SIDEBAR_GROUP_WIDTH },
  ];
};

type MissingItem = { id: string; start_time: Moment; type?: PRACTICE_TYPE; reason?: InvalidReason; year?: string };

const getMissingCropData = (
  fieldOutcomesByPractice: FieldOutcomesByPractice,
  relevantItems: TimelineCalendarItemsProps<CropItemData>[],
  practiceSlug: PracticeSlug
): MissingItem[] => {
  const isRequired = Object.entries(fieldOutcomesByPractice[practiceSlug]?.required || []).some((required) => required);
  if (!isRequired) return [];

  const missingData: MissingItem[] = [];

  relevantItems.forEach((relevantItem) => {
    const isRelevant = isRelevantYear(fieldOutcomesByPractice, practiceSlug, relevantItem.data?.year);
    const hasMissingCrop = isRelevant && (relevantItem.isPlaceholder || relevantItem.data?.cropName === "UNKNOWN");

    if (hasMissingCrop) {
      missingData.push({
        id: relevantItem.id.toString(),
        start_time: relevantItem.start_time,
        type: relevantItem.practiceType,
        year: relevantItem.data.year?.toString(),
      });
    }
  });

  return missingData.map((value) => value);
};

const getMissingSeedingData = (
  fieldOutcomesByPractice: FieldOutcomesByPractice,
  relevantItems: TimelineCalendarItemsProps<CropItemData>[],
  existingCrops: CropFCPO[],
  practiceSlug: PracticeSlug
): MissingItem[] => {
  const isRequired = Object.entries(fieldOutcomesByPractice[practiceSlug]?.required || []).some((required) => required);
  if (!isRequired) return [];

  const missingData: MissingItem[] = [];

  relevantItems.forEach((relevantItem) => {
    const practices = existingCrops.filter((crop) => {
      const hasSeeding = crop.seedingPracticeType;
      const sameYear = crop.year === relevantItem.data.year;
      return hasSeeding && sameYear;
    });
    const isRelevant = isRelevantYear(fieldOutcomesByPractice, practiceSlug, relevantItem.data?.year) && relevantItem.isPlaceholder;
    const hasDataForThatYear = practices.length;
    const hasMissingPractice = isRelevant && !hasDataForThatYear;

    if (hasMissingPractice) {
      missingData.push({
        id: relevantItem.id.toString(),
        start_time: relevantItem.start_time,
        type: PRACTICE_TYPE.SEEDING,
        year: relevantItem.data.year?.toString(),
      });
    }
  });

  return missingData.map((value) => value);
};

const getMissingPracticeData = (
  fieldOutcomesByPractice: FieldOutcomesByPractice,
  relevantItems: TimelineCalendarItemsProps<CropItemData>[],
  existingPractices: PracticeFCPO[],
  practiceSlug: PracticeSlug
): MissingItem[] => {
  const isRequired = Object.entries(fieldOutcomesByPractice[practiceSlug]?.required || []).some((required) => required);
  if (!isRequired) return [];

  const missingData: MissingItem[] = [];

  relevantItems.forEach((relevantItem) => {
    const practices = existingPractices.filter((practice) => {
      const samePracticeType = practice.type === practiceSlug;
      const sameYear = practice.year === relevantItem.data.year;
      return samePracticeType && sameYear;
    });
    const isRelevant = isRelevantYear(fieldOutcomesByPractice, practiceSlug, relevantItem.data?.year);
    const hasDataForThatYear = practices.length;
    const hasMissingPractice = isRelevant && !hasDataForThatYear;

    if (hasMissingPractice) {
      missingData.push({
        id: relevantItem.id.toString(),
        start_time: relevantItem.start_time,
        type: practiceSlug === PRACTICE_SLUG.TILLAGES ? PRACTICE_TYPE.TILLAGE : PRACTICE_TYPE.FERTILIZER,
        year: relevantItem.data.year?.toString(),
      });
    }
  });

  return missingData.map((value) => value);
};

export const getMissingIsMandatoryData = (
  existingCrops: CropFCPO[],
  isInRedzone: boolean,
  cropItems: TimelineCalendarItemsProps<CropItemData>[]
): MissingItem[] => {
  if (!isInRedzone) return [];

  const missingData: MissingItem[] = [];

  existingCrops.forEach((crop) => {
    const { cropType, mandatory, redzoneExceptionId } = crop;
    // Check if mandatory is false and redzoneExceptionId is null
    if (isCoverCropMissingRedzoneException(cropType, mandatory, redzoneExceptionId)) {
      // Find the corresponding timeline item to get the start_time and id
      const correspondingItem = cropItems.find((item) => (item.id === crop.id || item.cropItemId === crop.id) && item.data?.cropType === CROP_TYPE_FCPO.COVER);

      if (correspondingItem) {
        missingData.push({
          id: correspondingItem.id.toString(),
          start_time: correspondingItem.start_time,
          type: PRACTICE_TYPE.COVER_CROP,
          year: crop.year?.toString(),
        });
      }
    }
  });

  return missingData;
};

export const getEarliestItemIdWithMissingData = (
  carbonProgramOutcomes: CarbonProgramResult,
  fieldPracticeResponse: FieldPracticeResponse,
  cropItems: TimelineCalendarItemsProps<CropItemData>[],
  isInRedzone: boolean = false
) => {
  if (!carbonProgramOutcomes || "error" in carbonProgramOutcomes) return null;

  const relevantItems = cropItems.filter((item) => {
    if (!item.data?.year) return false;
    // remove all side crops before 2025 (historic side crop data, which practices could not be assigned to, so shouldn't be checked)
    if (item.data?.year < 2025 && item.data?.cropType !== CROP_TYPE_FCPO.MAIN) return false;
    // remove placeholder side crop items that are in the current or future years since they are not required
    if (item.data.cropType !== CROP_TYPE_FCPO.MAIN && item.isPlaceholder && item.data.year >= moment().year() && !item.data.isMandatory) return false;
    return true;
  });

  const missingCropData = getMissingCropData(carbonProgramOutcomes.fieldOutcomes, relevantItems, "crops");
  const missingSeedingData = getMissingSeedingData(carbonProgramOutcomes.fieldOutcomes, relevantItems, fieldPracticeResponse.crops, "seedings");
  const missingTillageData = getMissingPracticeData(
    carbonProgramOutcomes.fieldOutcomes,
    relevantItems,
    fieldPracticeResponse.practices,
    PRACTICE_SLUG.TILLAGES
  );
  const missingMineralFertilizerData = getMissingPracticeData(
    carbonProgramOutcomes.fieldOutcomes,
    relevantItems,
    fieldPracticeResponse.practices,
    PRACTICE_SLUG.MINERAL_FERTILIZERS
  );
  const missingOrganicFertilizerData = getMissingPracticeData(
    carbonProgramOutcomes.fieldOutcomes,
    relevantItems,
    fieldPracticeResponse.practices,
    PRACTICE_SLUG.ORGANIC_FERTILIZERS
  );

  const missingMandatoryData = getMissingIsMandatoryData(fieldPracticeResponse.crops, isInRedzone, relevantItems);

  const uniqueAndSortedMissingData = [
    ...missingCropData,
    ...missingSeedingData,
    ...missingTillageData,
    ...missingMineralFertilizerData,
    ...missingOrganicFertilizerData,
    ...missingMandatoryData,
  ]
    .reduce((previousValue, currentValue) => {
      const exists = previousValue.find((value) => value.type === currentValue.type && value.id === currentValue.id && value.year === currentValue.year);
      if (!exists) {
        previousValue.push(currentValue);
      }
      return previousValue;
    }, [] as MissingItem[])
    .sort((a, b) => a.start_time.valueOf() - b.start_time.valueOf());

  if (!uniqueAndSortedMissingData.length) return null;
  return uniqueAndSortedMissingData[0].id;
};

export const getCropsBeforeAndAfter = (
  fieldPracticeResponse?: FieldPracticeResponse,
  selectedRecommendationItemOnTimeline?: TimelineCalendarItemsProps<CropItemData>
): CMCMainCrops => {
  const mainCrops = fieldPracticeResponse?.crops.filter((crop) => crop.cropType === CROP_TYPE_FCPO.MAIN);

  if (!selectedRecommendationItemOnTimeline || !mainCrops || !mainCrops.length) {
    return { before: null, after: null };
  }

  const mainCropBefore = mainCrops.reduce(
    (latest, current) => {
      if (!current.harvestEndDate || !moment(current.harvestEndDate).isSameOrBefore(selectedRecommendationItemOnTimeline.start_time)) {
        return latest;
      }
      if (!latest || moment(current.harvestEndDate).isAfter(moment(latest.harvestEndDate))) {
        return current;
      }
      return latest;
    },
    null as CropFCPO | null
  );

  const mainCropAfter = mainCrops.reduce(
    (earliest, current) => {
      if (!current.seedingStartDate || !moment(current.seedingStartDate).isSameOrAfter(selectedRecommendationItemOnTimeline.end_time)) {
        return earliest;
      }
      if (!earliest || moment(current.seedingStartDate).isBefore(moment(earliest.seedingStartDate))) {
        return current;
      }
      return earliest;
    },
    null as CropFCPO | null
  );

  return { before: mainCropBefore, after: mainCropAfter };
};

export const getNextCrop = (
  fieldPracticeResponse?: FieldPracticeResponse,
  selectedRecommendationItemOnTimeline?: TimelineCalendarItemsProps<CropItemData>
): CropFCPO | undefined => {
  const mainAndCoverCrops =
    fieldPracticeResponse?.crops.filter((crop) => crop.cropType === CROP_TYPE_FCPO.MAIN || crop.cropType === CROP_TYPE_FCPO.COVER) || [];

  if (!selectedRecommendationItemOnTimeline || !mainAndCoverCrops) return;

  return mainAndCoverCrops
    .filter((crop) => crop.year && crop.year > selectedRecommendationItemOnTimeline.end_time.year())
    .sort((a, b) => (moment(a.seedingStartDate).isBefore(b.seedingStartDate) ? -1 : 1))?.[0];
};

export const calculateImpactPanelBodyHeight = () => {
  const headerDesktop = document.querySelector(".header-desktop.container");
  const impactPanelHeader = document.querySelector(".field-timeline-impact-panel.header");

  const headerDesktopHeight = headerDesktop ? headerDesktop.getBoundingClientRect().height : 0;
  const impactPanelHeaderHeight = impactPanelHeader ? impactPanelHeader.getBoundingClientRect().height : 0;

  const bodyHeightRem = (window.innerHeight - headerDesktopHeight - impactPanelHeaderHeight - 1) / 16;

  return bodyHeightRem;
};

export const getTimelineDefaultStartAndEnd = (
  farmReportingYearData: number | undefined,
  carbonProgram: CarbonProgram | null,
  screenSize: "small" | "medium" | "large" = "large"
): { start: number; end: number } => {
  const { startDate: reportingPeriodStartDate } = getCurrentReportingValues(farmReportingYearData ?? moment().year(), carbonProgram);
  const startDate = reportingPeriodStartDate || moment().startOf("year");
  const monthsToSubtractToStart = screenSize === "small" ? 2 : screenSize === "medium" ? 4 : 6;
  const timelineLengthInMonths = screenSize === "small" ? 12 : screenSize === "medium" ? 18 : 24;
  const timelineLengthInMilliseconds = moment.duration(timelineLengthInMonths, "months").asMilliseconds();

  return {
    start: startDate.clone().subtract(monthsToSubtractToStart, "months").valueOf(),
    end: startDate.clone().add(timelineLengthInMilliseconds).valueOf(),
  };
};
